import React, { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import { auth, db } from "../firebase";
import { sendPasswordResetEmail } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile
} from "firebase/auth";
import { doc, getDoc, updateDoc, setDoc, collection, query, where, getDocs, arrayUnion, arrayRemove } from "firebase/firestore";

const AVATAR_OPTIONS = ["👤", "🧙‍♂️", "🥷", "🤖", "👽", "👾", "🐉", "🃏", "👑", "👻"];

export default function Account({socket}) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isLoginView, setIsLoginView] = useState(true);
  
  // Navigation
  const [activeTab, setActiveTab] = useState("profile"); // "profile" oder "friends"

  // Formular States
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Profil bearbeiten
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editBio, setEditBio] = useState("");
  const [editAvatar, setEditAvatar] = useState("👤");

  // Freundessystem States
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [globalOnlineUsers, setGlobalOnlineUsers] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const docRef = doc(db, "users", currentUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserData(data);
          setEditName(currentUser.displayName || "");
          setEditBio(data.bio || "");
          setEditAvatar(data.avatar || "👤");
        } else {
          setUserData({});
          setEditName(currentUser.displayName || "");
        }
      } else {
        setUserData(null);
      }
    });
    return () => unsubscribe();
  }, []);
  useEffect(() => {
  if (!socket || !user) return;
  socket.emit("user_login", { uid: user.uid, name: user.displayName });
  
  socket.on("online_users_update", (users) => {
    setGlobalOnlineUsers(users);
  });
  
  return () => socket.off("online_users_update");
}, [socket, user]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLoginView) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        if (username.trim().length < 3) throw new Error("Name must be at least 3 characters.");
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(userCredential.user, { displayName: username });
        
        // Firestore Dokument anlegen (inkl. leerer Freundeslisten)
        await setDoc(doc(db, "users", userCredential.user.uid), {
          displayName: username,
          avatar: "👤",
          bio: "New Player",
          wins: 0,
          friends: [],
          friendRequests: []
        });
        setUser({ ...userCredential.user, displayName: username });
      }
    } catch (err) {
      console.error(err);
      if (err.code === "auth/email-already-in-use") setError("Email is already in use.");
      else if (err.code === "auth/invalid-credential") setError("Wrong Email or Password.");
      else if (err.code === "auth/weak-password") setError("Password must be at least 6 characters.");
      else setError(err.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (editName.trim().length < 3) return setError("Name must be at least 3 characters.");
    setLoading(true); setError("");
    try {
      if (editName !== user.displayName) await updateProfile(user, { displayName: editName });
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { displayName: editName, bio: editBio, avatar: editAvatar });
      setUser({ ...user, displayName: editName });
      setUserData({ ...userData, bio: editBio, avatar: editAvatar });
      setIsEditing(false);
    } catch (err) {
      console.error(err); setError("Could not update profile.");
    }
    setLoading(false);
  };

  const handleLogout = async () => await signOut(auth);

  // ==========================================
  // FREUNDESSYSTEM LOGIK
  // ==========================================
  const searchFriends = async () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    try {
      // Sucht nach exakt diesem Anzeigenamen
      const q = query(collection(db, "users"), where("displayName", "==", searchQuery.trim()));
      const querySnapshot = await getDocs(q);
      const results = [];
      querySnapshot.forEach((doc) => {
        // Sich selbst nicht in den Ergebnissen anzeigen
        if (doc.id !== user.uid) results.push({ id: doc.id, ...doc.data() });
      });
      setSearchResults(results);
    } catch (err) {
      console.error("Search failed", err);
    }
    setSearchLoading(false);
  };

  const sendFriendRequest = async (targetId) => {
    try {
      const targetRef = doc(db, "users", targetId);
      // Anfrage in die DB des Ziels schreiben
      await updateDoc(targetRef, {
        friendRequests: arrayUnion({
          uid: user.uid,
          name: user.displayName,
          avatar: userData?.avatar || "👤"
        })
      });
      alert("Friend request sent!");
      setSearchResults([]);
    } catch (err) {
      console.error(err);
      alert("Error sending request.");
    }
  };

  const acceptRequest = async (request) => {
    try {
      // Sicherheits-Fallback für undefined Avatare
      const safeAvatar = request.avatar || "👤";
      const mySafeAvatar = userData?.avatar || "👤";

      // 1. Sich selbst beim anderen eintragen
      const senderRef = doc(db, "users", request.uid);
      await updateDoc(senderRef, {
        friends: arrayUnion({ uid: user.uid, name: user.displayName, avatar: mySafeAvatar })
      });

      // 2. Den anderen bei sich eintragen UND die Anfrage löschen
      const myRef = doc(db, "users", user.uid);
      await updateDoc(myRef, {
        friends: arrayUnion({ uid: request.uid, name: request.name, avatar: safeAvatar }),
        friendRequests: arrayRemove({ uid: request.uid, name: request.name, avatar: safeAvatar }) // Hier explizit das Objekt neu bauen, um exakten DB-Match zu garantieren
      });

      // Lokalen State updaten
      setUserData(prev => ({
        ...prev,
        friendRequests: prev.friendRequests.filter(r => r.uid !== request.uid),
        friends: [...(prev.friends || []), { uid: request.uid, name: request.name, avatar: safeAvatar }]
      }));
    } catch (err) {
      console.error("Fehler beim Annehmen:", err);
    }
  };

  const declineRequest = async (request) => {
    try {
      const myRef = doc(db, "users", user.uid);
      await updateDoc(myRef, { friendRequests: arrayRemove(request) });
      setUserData(prev => ({
        ...prev, friendRequests: prev.friendRequests.filter(r => r.uid !== request.uid)
      }));
    } catch (err) {
      console.error(err);
    }
  };


  return (
    <div style={{ padding: "80px 20px", color: "white", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", backgroundImage: "url('/background.png')", backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}>
      <Helmet><title>Account | UN-LOCKED</title></Helmet>

      <h1 style={{ fontSize: "4em", margin: "0 0 30px 0", color: "gold", textShadow: "0 4px 10px rgba(0,0,0,0.8)" }}>ACCOUNT</h1>

      <div style={{ backgroundColor: "rgba(30, 30, 30, 0.8)", border: "4px solid gold", borderRadius: 20, padding: "40px", width: "100%", maxWidth: "600px", backdropFilter: "blur(10px)", boxShadow: "0 10px 30px rgba(0,0,0,0.5)", textAlign: "center" }}>
        
        {user ? (
          <div>
            {/* TABS NAVIGATION */}
            <div style={{ display: "flex", gap: "10px", marginBottom: "30px", borderBottom: "2px solid #555", paddingBottom: "10px" }}>
              <button onClick={() => setActiveTab("profile")} style={{ ...tabStyle, borderBottom: activeTab === "profile" ? "3px solid gold" : "none", color: activeTab === "profile" ? "gold" : "white" }}>
                Profile
              </button>
              <button onClick={() => setActiveTab("friends")} style={{ ...tabStyle, borderBottom: activeTab === "friends" ? "3px solid gold" : "none", color: activeTab === "friends" ? "gold" : "white", position: "relative" }}>
                Friends
                {userData?.friendRequests?.length > 0 && (
                  <span style={{ position: "absolute", top: -5, right: -10, backgroundColor: "red", color: "white", borderRadius: "50%", padding: "2px 6px", fontSize: "0.7em", fontWeight: "bold" }}>
                    {userData.friendRequests.length}
                  </span>
                )}
              </button>
            </div>

            {/* TAB INHALT: PROFILE */}
            {activeTab === "profile" && (
              <div>
                {!isEditing ? (
                  <div style={{ marginBottom: "30px", position: "relative" }}>
                    <button onClick={() => setIsEditing(true)} style={{ position: "absolute", top: 0, right: 0, background: "transparent", border: "1px solid #777", color: "#aaa", padding: "5px 10px", borderRadius: "5px", cursor: "pointer" }}>✏️ Edit</button>
                    <div style={{ fontSize: "5em", margin: "0 auto 10px auto", background: "rgba(0,0,0,0.5)", width: "120px", height: "120px", borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center", border: "3px solid gold" }}>{userData?.avatar || "👤"}</div>
                    <h2 style={{ margin: "0 0 5px 0", fontSize: "2.5em" }}><span style={{ color: "gold" }}>{user.displayName}</span></h2>
                    <p style={{ color: "#aaa", fontStyle: "italic", margin: "0 0 15px 0" }}>"{userData?.bio || "Ready to play!"}"</p>
                  </div>
                ) : (
                  <div style={{ marginBottom: "30px", backgroundColor: "rgba(0,0,0,0.5)", padding: "20px", borderRadius: "15px", border: "1px solid #555" }}>
                    <h3 style={{ color: "gold", marginTop: 0 }}>Edit Profile</h3>
                    {error && <div style={{ color: "red", marginBottom: "10px" }}>{error}</div>}
                    <div style={{ marginBottom: "20px" }}>
                      <p style={{ margin: "0 0 10px 0", color: "#aaa" }}>Choose Avatar:</p>
                      <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
                        {AVATAR_OPTIONS.map(emoji => (
                          <div key={emoji} onClick={() => setEditAvatar(emoji)} style={{ fontSize: "2em", cursor: "pointer", padding: "5px", borderRadius: "10px", backgroundColor: editAvatar === emoji ? "rgba(255, 215, 0, 0.3)" : "transparent", border: editAvatar === emoji ? "2px solid gold" : "2px solid transparent", transition: "all 0.2s" }}>{emoji}</div>
                        ))}
                      </div>
                    </div>
                    <input type="text" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Username" style={{ ...inputStyle, width: "100%", marginBottom: "10px", boxSizing: "border-box" }} />
                    <input type="text" value={editBio} onChange={e => setEditBio(e.target.value)} placeholder="Short Bio / Motto" maxLength={40} style={{ ...inputStyle, width: "100%", marginBottom: "20px", boxSizing: "border-box" }} />
                    <div style={{ display: "flex", gap: "10px" }}>
                      <button onClick={handleSaveProfile} disabled={loading} style={{ flex: 1, padding: "10px", backgroundColor: "green", color: "white", fontWeight: "bold", border: "none", borderRadius: "8px", cursor: "pointer" }}>Save ✔️</button>
                      <button onClick={() => { setIsEditing(false); setError(""); }} style={{ flex: 1, padding: "10px", backgroundColor: "#555", color: "white", fontWeight: "bold", border: "none", borderRadius: "8px", cursor: "pointer" }}>Cancel ❌</button>
                    </div>
                  </div>
                )}

                {/* STATS */}
                {userData && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginBottom: 30 }}>
                    <div style={statBoxStyle}><div style={statTitleStyle}>Games Played</div><div style={statValueStyle}>{userData.gamesPlayed || 0}</div></div>
                    <div style={statBoxStyle}><div style={statTitleStyle}>Rounds Won 🏆</div><div style={statValueStyle}>{userData.wins || 0}</div></div>
                    <div style={statBoxStyle}><div style={statTitleStyle}>Total Gold 💰</div><div style={statValueStyle}>{userData.totalGold || 0}</div></div>
                    <div style={statBoxStyle}><div style={statTitleStyle}>Cards Discovered 🃏</div><div style={statValueStyle}>{userData.discoveredCards?.length || 0} / 50</div></div>
                  </div>
                )}
                {!isEditing && <button onClick={handleLogout} style={{ padding: "15px 30px", fontSize: "1.2em", backgroundColor: "darkred", color: "white", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: "bold", width: "100%" }}>Logout 🚪</button>}
              </div>
            )}

            {/* TAB INHALT: FRIENDS */}
            {activeTab === "friends" && (
              <div style={{ textAlign: "left" }}>
                
                {/* 1. SUCHE */}
                <h3 style={{ color: "gold", marginTop: 0 }}>Add Friends</h3>
                <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
                  <input type="text" placeholder="Exact Username..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchFriends()} style={{ ...inputStyle, flex: 1 }} />
                  <button onClick={searchFriends} disabled={searchLoading} style={{ padding: "0 20px", backgroundColor: "gold", color: "black", fontWeight: "bold", border: "none", borderRadius: "8px", cursor: "pointer" }}>🔍</button>
                </div>
                
                {/* SUCH-ERGEBNISSE */}
                {searchResults.length > 0 && (
                  <div style={{ backgroundColor: "rgba(0,0,0,0.5)", padding: "15px", borderRadius: "10px", marginBottom: "30px", border: "1px solid #555" }}>
                    <h4 style={{ margin: "0 0 10px 0", color: "#aaa" }}>Results:</h4>
                    {searchResults.map(res => (
                      <div key={res.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px", backgroundColor: "#222", borderRadius: "8px", marginBottom: "5px" }}>
                        <div style={{ fontSize: "1.2em", fontWeight: "bold" }}>{res.avatar} {res.displayName}</div>
                        <button onClick={() => sendFriendRequest(res.id)} style={{ padding: "5px 10px", backgroundColor: "green", color: "white", border: "none", borderRadius: "5px", cursor: "pointer", fontWeight: "bold" }}>Add ➕</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* 2. FREUNDSCHAFTSANFRAGEN (Repariert) */}
                {userData?.friendRequests?.length > 0 && (
                  <div style={{ marginBottom: "30px" }}>
                    <h3 style={{ color: "gold" }}>Friend Requests</h3>
                    {(userData?.friendRequests || []).map(req => (
                      <div key={req.uid} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px", backgroundColor: "rgba(100,0,0,0.3)", border: "1px solid darkred", borderRadius: "8px", marginBottom: "5px" }}>
                        <div style={{ fontSize: "1.2em", fontWeight: "bold" }}>{req.avatar} {req.name}</div>
                        <div style={{ display: "flex", gap: "5px" }}>
                          <button onClick={() => acceptRequest(req)} style={{ padding: "5px 10px", backgroundColor: "green", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" }}>✔️</button>
                          <button onClick={() => declineRequest(req)} style={{ padding: "5px 10px", backgroundColor: "darkred", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" }}>❌</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* 3. MEINE FREUNDE (Repariert) */}
                <h3 style={{ color: "gold" }}>My Friends</h3>
                {(!userData?.friends || userData.friends.length === 0) ? (
                  <div style={{ color: "gray", fontStyle: "italic", textAlign: "center", padding: "20px" }}>No friends yet. Add some!</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {(userData?.friends || []).map(friend => {
                      const isOnlineObj = globalOnlineUsers[friend.uid];
                      const isOnline = !!isOnlineObj;
                      const isPlaying = isOnlineObj?.status === "In Game";

                      return (
                        <div key={friend.uid} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "15px", backgroundColor: "#222", borderRadius: "8px", border: "1px solid #444" }}>
                          <div 
                            onClick={() => navigate(`/account/${friend.name}`)}
                            style={{ fontSize: "1.2em", fontWeight: "bold", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", transition: "color 0.2s" }}
                            onMouseEnter={(e) => e.currentTarget.style.color = "gold"}
                            onMouseLeave={(e) => e.currentTarget.style.color = "white"}
                          >
                            <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: isPlaying ? "orange" : isOnline ? "lime" : "gray", boxShadow: `0 0 8px ${isPlaying ? "orange" : isOnline ? "lime" : "transparent"}` }} />
                            {friend.avatar} <span style={{ textDecoration: "underline" }}>{friend.name}</span>
                            <span style={{ fontSize: "0.7em", color: "#888", textDecoration: "none" }}>{isPlaying ? "(In Game)" : isOnline ? "(Online)" : "(Offline)"}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleAuth} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* LOGIN FORMULAR BLEIBT UNVERÄNDERT */}
            <h2 style={{ margin: 0, fontSize: "2em" }}>{isLoginView ? "Login" : "Register"}</h2>
            {error && <div style={{ backgroundColor: "rgba(255,0,0,0.2)", color: "#ff6666", padding: 10, borderRadius: 8, border: "1px solid red", fontWeight: "bold" }}>{error}</div>}
            {!isLoginView && <input type="text" placeholder="Your Username" required value={username} onChange={e => setUsername(e.target.value)} style={inputStyle} />}
            <input type="email" placeholder="Email" required value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
            <input type="password" placeholder="Password (min. 6 characters)" required value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} />
            <button type="submit" disabled={loading} style={{ padding: "15px", fontSize: "1.2em", backgroundColor: "gold", color: "black", border: "none", borderRadius: 10, cursor: loading ? "not-allowed" : "pointer", fontWeight: "bold", marginTop: 10 }}>
              {loading ? "Loading..." : (isLoginView ? "Login ➡️" : "Create Account ➡️")}
            </button>
            {isLoginView && (
              <div 
                onClick={async () => {
                  if (!email) return alert("Bitte gib zuerst deine E-Mail oben ein!");
                  try {
                    await sendPasswordResetEmail(auth, email);
                    alert("Eine E-Mail zum Zurücksetzen wurde gesendet!");
                  } catch (err) {
                    alert("Fehler: " + err.message);
                  }
                }}
                style={{ color: "#aaa", cursor: "pointer", fontSize: "0.9em", marginTop: "-10px" }}
              >
                Forgot Password?
              </div>
            )}
            <div onClick={() => { setIsLoginView(!isLoginView); setError(""); }} style={{ color: "#aaa", cursor: "pointer", textDecoration: "underline", marginTop: 10 }}>
              {isLoginView ? "No account yet? Register here." : "Already have an account? Login here."}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// STYLING OBJEKTE
const inputStyle = { padding: "15px", fontSize: "1.1em", borderRadius: 8, border: "2px solid #555", backgroundColor: "rgba(10, 10, 10, 0.8)", color: "white", outline: "none" };
const statBoxStyle = { backgroundColor: "rgba(20, 20, 20, 0.8)", border: "1px solid #555", borderRadius: "10px", padding: "15px", display: "flex", flexDirection: "column", alignItems: "center" };
const statTitleStyle = { color: "#aaa", fontSize: "0.9em", textTransform: "uppercase", fontWeight: "bold", marginBottom: "5px" };
const statValueStyle = { color: "gold", fontSize: "1.8em", fontWeight: "900" };
const tabStyle = { flex: 1, padding: "15px", backgroundColor: "transparent", border: "none", fontSize: "1.2em", fontWeight: "bold", cursor: "pointer", transition: "all 0.2s" };